import React, { useState, useEffect } from 'react';
import logo from "../assets/logo.png";
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaCog, FaBell, FaTimes } from 'react-icons/fa';

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
  padding: 10px 16px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const LogoImage = styled.img`
  width: 80px;
  height: 70px;
`;

const BrandName = styled.span`
  font-size: 1.25rem;
  font-weight: bold;
  color: #000;
  margin-left: 8px;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  font-size: 1.75rem;
  color: #000;
  cursor: pointer;
  padding: 0;
  margin-right: 10px;
  line-height: 1;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.7;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background-color: #dc3545;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;

const LogoutButton = styled.button`
  background-color: #dc3545;
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background-color: rgb(167, 20, 35);
  }
`;

const Header = ({ onLogout }) => {
  const navigate = useNavigate();
  const [notificationCount, setNotificationCount] = useState(0);

  // Fetch notification count from backend API
  const fetchNotificationCount = async () => {
    try {
      const response = await fetch('/api/notification-count', {
        method: 'GET',
        credentials: 'include', // Include cookies for JWT
      });

      if (response.ok) {
        const data = await response.json();
        setNotificationCount(data.count || 0);
      } else {
        console.error('Failed to fetch notification count');
        setNotificationCount(0);
      }
    } catch (error) {
      console.error('Error fetching notification count:', error);
      setNotificationCount(0);
    }
  };

  useEffect(() => {
    fetchNotificationCount();
    
    // Optionally: Poll for updates every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleNotificationClick = async () => {
    // Mark all notifications as seen when bell button is clicked
    try {
      const markSeenResponse = await fetch('/api/mark-notifications-seen', {
        method: 'POST',
        credentials: 'include', // Include cookies for JWT
      });

      if (markSeenResponse.ok) {
        const data = await markSeenResponse.json();
        console.log(`Marked ${data.updated_count} notifications as seen`);
        // Refresh notification count after marking as seen
        fetchNotificationCount();
      } else {
        console.error('Failed to mark notifications as seen');
      }
    } catch (err) {
      console.error('Error marking notifications as seen:', err);
    }

    // Navigate to inbox page
    navigate('/inbox');
  };

  return (
    <HeaderWrapper>
      <div className="d-flex align-items-center">
        <LogoSection onClick={() => navigate('/Dashboard')}>
          <LogoImage src={logo} alt="Codelattice Logo" />
          <BrandName>Codelattice</BrandName>
        </LogoSection>
      </div>

      {onLogout && (
        <RightSection>
          <IconButton onClick={handleSettingsClick} title="Settings">
            <FaCog size={18} />
          </IconButton>
          
          <IconButton onClick={handleNotificationClick} title="Notifications">
            <FaBell size={18} />
            {notificationCount > 0 && (
              <NotificationBadge>{notificationCount > 9 ? '9+' : notificationCount}</NotificationBadge>
            )}
          </IconButton>
          
          <LogoutButton onClick={onLogout}>
            Logout
          </LogoutButton>
        </RightSection>
      )}
    </HeaderWrapper>
  );
};

export default Header;